import { useState } from 'react';
import { useCreateAgentAlias, useDeleteAgentAlias } from '../../hooks/useAgents';
import { Button, Card, CardHeader, ConfirmModal, Input, Modal, Select } from '@/components/ui';
import { useToast } from '../ui/Toast';
import type { AgentAlias, AgentVersion } from '../../types';

interface AliasManagerProps {
  agentId: string;
  aliases: AgentAlias[];
  versions: AgentVersion[];
}

export default function AliasManager({
  agentId,
  aliases,
  versions,
}: AliasManagerProps) {
  const { success, error: showError } = useToast();
  const createAlias = useCreateAgentAlias();
  const deleteAlias = useDeleteAgentAlias();

  const [createModal, setCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    aliasName: '',
    targetVersion: '',
    description: '',
  });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    alias: AgentAlias | null;
  }>({ isOpen: false, alias: null });

  const versionOptions = versions
    .filter((v) => v.version != null)
    .map((v) => ({
      value: v.version.toString(),
      label: `Version ${v.version}${v.description ? ` - ${v.description}` : ''}`,
    }));

  const handleCreate = async () => {
    if (!formData.aliasName || !formData.targetVersion) return;

    try {
      await createAlias.mutateAsync({
        agentId,
        aliasName: formData.aliasName,
        targetVersion: parseInt(formData.targetVersion),
        description: formData.description || undefined,
      });
      success('Alias created successfully');
      setCreateModal(false);
      setFormData({ aliasName: '', targetVersion: '', description: '' });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create alias');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.alias) return;

    try {
      await deleteAlias.mutateAsync({
        agentId,
        aliasName: deleteModal.alias.aliasName,
      });
      success('Alias deleted successfully');
      setDeleteModal({ isOpen: false, alias: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete alias');
    }
  };

  const handleUpdateAlias = async (alias: AgentAlias, newVersion: number) => {
    try {
      await createAlias.mutateAsync({
        agentId,
        aliasName: alias.aliasName,
        targetVersion: newVersion,
        description: alias.description,
      });
      success('Alias updated successfully');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update alias');
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Aliases"
          description="Named references to specific versions"
          action={
            versions.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCreateModal(true)}
              >
                Create Alias
              </Button>
            )
          }
        />
        {versions.length === 0 ? (
          <p className="text-muted-foreground">
            Publish a version first before creating aliases.
          </p>
        ) : aliases.length === 0 ? (
          <p className="text-muted-foreground">
            No aliases created yet. Create an alias to reference a specific version.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {aliases.map((alias) => (
              <div
                key={alias.aliasName}
                className="py-4 first:pt-0 last:pb-0 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-foreground">{alias.aliasName}</p>
                  {alias.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {alias.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Updated {new Date(alias.updatedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Points to:</span>
                    <Select
                      options={versionOptions}
                      value={alias.targetVersion?.toString() ?? ''}
                      onChange={(e: { target: { value: string } }) =>
                        handleUpdateAlias(alias, parseInt(e.target.value))
                      }
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteModal({ isOpen: true, alias })}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create alias modal */}
      <Modal
        isOpen={createModal}
        onClose={() => setCreateModal(false)}
        title="Create Alias"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              isLoading={createAlias.isPending}
              disabled={!formData.aliasName || !formData.targetVersion}
            >
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Alias Name"
            value={formData.aliasName}
            onChange={(e: { target: { value: string } }) =>
              setFormData((prev) => ({ ...prev, aliasName: e.target.value }))
            }
            placeholder="production"
            hint="Alphanumeric characters and hyphens only"
          />
          <Select
            label="Target Version"
            options={versionOptions}
            value={formData.targetVersion}
            onChange={(e: { target: { value: string } }) =>
              setFormData((prev) => ({ ...prev, targetVersion: e.target.value }))
            }
            placeholder="Select a version"
          />
          <Input
            label="Description (optional)"
            value={formData.description}
            onChange={(e: { target: { value: string } }) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Production-ready version"
          />
        </div>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, alias: null })}
        onConfirm={handleDelete}
        title="Delete Alias"
        message={`Are you sure you want to delete the alias "${deleteModal.alias?.aliasName}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteAlias.isPending}
      />
    </>
  );
}
